import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { offerFormSchema, OfferFormValues, toFormRegEvents } from "../schemas/offerFormSchema";
import { Offer } from "@/types/offer";
import { CampLocation } from "@/types/campLocation";
import { ensureValidDate } from "@/utils/dateUtils";
import { useDecimalEnums } from "@/components/admin/locations/hooks/useDecimalEnums";
import { useFieldModification } from "./useFieldModification";
import { usePublishedLocation } from "./usePublishedLocation";
import { useOfferFormInitialization } from "./useOfferFormInitialization";
import { useProgramFieldManagement } from "./useProgramFieldManagement";
import { useEffect } from "react";
import { useRef } from "react";
import { addDays, isSameDay } from "date-fns";
import { prefillSessionLinks } from "../utils/sessionLinkPrefill";

interface UseOfferFormProps {
  initialOffer?: Offer | null;
  initialDate?: Date;
  lastOfferByVariation?: Record<string, Offer>;
  location: CampLocation;
  usePublishedData?: boolean;
  savedFormData?: OfferFormValues | null;
}

export const useOfferForm = ({
  initialOffer,
  initialDate,
  location,
  lastOfferByVariation = {},
  usePublishedData = true, // Always use published data
  savedFormData = null
}: UseOfferFormProps) => {
  // Get decimal enums for number of days calculation
  const { enumsMap } = useDecimalEnums();
  
  // Ensure we're using the published location
  const { verifiedLocation, isLocationLoaded } = usePublishedLocation(location);
  
  
  // Initialize form with default values from saved data, existing offer, or new defaults
  const getDefaultValues = (): any => {
    // Priority: savedFormData > initialOffer > new defaults
    if (savedFormData) {
      return savedFormData;
    }
    
    if (initialOffer) {
      
      // CRITICAL DEBUG: Log the exact values being loaded for session length
      
      return {
        variationKey: initialOffer.variationKey,
        displayTitle: initialOffer.displayTitle,
        description: initialOffer.description || "",
        eligibilityNotes: initialOffer.eligibilityNotes || "",
        startDate: initialOffer.startDate instanceof Date ? initialOffer.startDate : ensureValidDate(initialOffer.startDate),
        endDate: initialOffer.endDate instanceof Date ? initialOffer.endDate : ensureValidDate(initialOffer.endDate),
        priceOverride: initialOffer.priceOverride,
        priceRegular: initialOffer.priceRegular,
        currentSalePrice: initialOffer.currentSalePrice,
        pricingTierPriceOverrides: (() => {
          const raw: any = (initialOffer as any).pricingTierPriceOverrides ?? null;
          if (!raw || typeof raw !== "object") return null;
          // Back-compat: allow saved maps without `v`
          return { v: 1, ...raw };
        })(),
        // CRITICAL FIX: Handle priceNoteOverride correctly: null means use program value, "" means show empty string
        priceNoteOverride: initialOffer.priceNoteOverride,
        // CRITICAL FIX: Handle timingNoteOverride correctly: null means use program value, "" means show empty string  
        timingNoteOverride: initialOffer.timingNoteOverride,
        availabilityStatus: initialOffer.availabilityStatus || "av_unknown",
        signupStatus: initialOffer.signupStatus || "su_unknown",
        visibility: "published",
        variation: null, // Will be set in useEffect
        numDaysOverride: initialOffer.numDaysOverride,
        myOfferIncrementKey: (initialOffer as any).myOfferIncrementKey || null,
        url: initialOffer.url || "",
        regUrl: initialOffer.regUrl || "",
        // KB-2673 — via the shared mapper so `whenIso` / `zoneLabel` survive an
        // admin edit. The previous inline four-field literal dropped both.
        regEvents: toFormRegEvents(initialOffer.regEvents as any, ensureValidDate),
        // Seed override UI fields based on flag
        overrideAges: !!(initialOffer as any).agesAreOverrides,
        ...( (() => {
          const selectedProgram = verifiedLocation?.variations?.find(v => v.key === initialOffer.variationKey) || null;
          const seedFrom: any = (initialOffer as any).agesAreOverrides ? (initialOffer as any).variationData : (selectedProgram || {});
          return {
            minAgeInYearsOverride: seedFrom?.minAgeInYears ?? null,
            maxAgeInYearsOverride: seedFrom?.maxAgeInYears ?? null,
            minAgeGradeKeyOverride: seedFrom?.minAgeGradeKey || "",
            maxAgeGradeKeyOverride: seedFrom?.maxAgeGradeKey || "",
            minAgeNoteOverride: seedFrom?.minAgeNote || "",
            maxAgeNoteOverride: seedFrom?.maxAgeNote || "",
            showMinAgeGradeAsPrimaryOverride: !!seedFrom?.showMinAgeGradeAsPrimary,
            showMaxAgeGradeAsPrimaryOverride: !!seedFrom?.showMaxAgeGradeAsPrimary
          };
        })() )
      };
    }
    
    return {
      variationKey: "",
      displayTitle: "",
      description: "",
      eligibilityNotes: "",
      startDate: initialDate || new Date(),
      endDate: initialDate || new Date(),
      priceOverride: undefined,
      priceRegular: undefined,
      currentSalePrice: undefined,
      pricingTierPriceOverrides: null,
      priceNoteOverride: "",
      timingNoteOverride: "",
      availabilityStatus: "av_unknown",
      signupStatus: "su_unknown",
      visibility: "published",
      variation: null,
      numDaysOverride: null,
      myOfferIncrementKey: null,
      url: "",
      regUrl: "",
      regEvents: []
    };
  };
  
  // Initialize form with default values or values from existing offer
  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerFormSchema),
    defaultValues: getDefaultValues()
  });

  // Use field modification tracking hook
  const { markFieldAsModified, resetFieldModified, isFieldModified } = useFieldModification();
  
  // Use form initialization hook
  const { isInitialized, resetDirtyState } = useOfferFormInitialization({
    form,
    initialOffer,
    verifiedLocation,
    enumsMap,
    isLocationLoaded,
    resetFieldModified
  });
  
  // Use program field management hook
  const { resetFieldToProgram, isEqualToProgramValue } = useProgramFieldManagement({
    form,
    verifiedLocation,
    enumsMap,
    resetFieldModified
  });

  // Use useWatch to get the current values of startDate, variationKey, and numDaysOverride
  const startDate = useWatch({
    control: form.control,
    name: "startDate",
  });
  
  const variationKey = useWatch({
    control: form.control,
    name: "variationKey",
  });

  const numDaysOverride = useWatch({
    control: form.control,
    name: "numDaysOverride",
  });

  // NEW: Auto-calculate End date ONLY when Start date changes (not when session length changes)
  // For existing sessions, honor the DB endDate on initial open by skipping the first auto-calc
  const skipFirstAutoCalcRef = useRef(false);
  const lastAutoCalcStartRef = useRef<number | null>(null);
  // Track last variation key to avoid overwriting values on initial modal open in edit mode
  const previousVariationKeyRef = useRef<string | null>(null);
  useEffect(() => {
    // Only auto-calculate when we have a valid start date
    const startMs = startDate instanceof Date && !isNaN(startDate?.getTime?.())
      ? startDate.getTime()
      : null;

    // On first run for existing offers: seed lastAutoCalcStartRef and skip
    if (initialOffer?.id && !skipFirstAutoCalcRef.current) {
      skipFirstAutoCalcRef.current = true;
      if (startMs !== null) lastAutoCalcStartRef.current = startMs;
      return;
    }

    // If start date value hasn't actually changed, don't recalc
    if (startMs !== null && lastAutoCalcStartRef.current === startMs) {
      return;
    }
    if (startMs !== null) {
      lastAutoCalcStartRef.current = startMs;
    }

    if (startDate && startDate instanceof Date && !isNaN(startDate.getTime())) {
      // Get current values at the time of start date change
      const currentNumDaysOverride = form.getValues('numDaysOverride');
      const currentVariationKey = form.getValues('variationKey');
      
      // Determine the number of days to use for calculation
      let daysToUse = null;
      
      // Priority 1: Use numDaysOverride if available (from session length selector in UI)
      if (currentNumDaysOverride && currentNumDaysOverride > 0) {
        daysToUse = currentNumDaysOverride;
      } 
      // Priority 2: Use program's numDays if available and no session length override
      else if (currentVariationKey && verifiedLocation.variations) {
        const selectedProgram = verifiedLocation.variations.find(v => v.key === currentVariationKey);
        if (selectedProgram && selectedProgram.numDays && selectedProgram.numDays > 0) {
          daysToUse = selectedProgram.numDays;
        }
      }
      
      // Calculate and set end date if we have a valid number of days
      if (daysToUse && daysToUse > 0) {
        const newEndDate = addDays(new Date(startDate), daysToUse - 1);
        const currentEndDate = form.getValues('endDate');
        
        // Only update if the calculated date is different from current end date
        if (!currentEndDate || !isSameDay(newEndDate, new Date(currentEndDate))) {
          
          form.setValue('endDate', newEndDate, { shouldDirty: true });
          form.trigger('endDate');
        }
      }
    }
  }, [startDate]); // ONLY trigger on startDate changes

  // Auto-populate session fields based on program when program selection changes
  useEffect(() => {
    // Only proceed if we have a program selected
    if (variationKey && verifiedLocation.variations) {
      // Guard: Do not auto-populate on initial open in edit mode. Only populate after user changes program.
      const prevVariationKey = previousVariationKeyRef.current;
      previousVariationKeyRef.current = variationKey;
      if (!prevVariationKey && initialOffer) {
        // First run for edit modal with preselected program — skip population
        return;
      }
      // If variation hasn't actually changed, do nothing
      if (prevVariationKey && prevVariationKey === variationKey) {
        return;
      }

      const selectedProgram = verifiedLocation.variations.find(v => v.key === variationKey);
      
      if (selectedProgram) {
        
        // CRITICAL: End date calculation is now handled by the dedicated auto-calc effect above
        // This prevents conflicts between multiple useEffects trying to set the end date
        
        // Auto-populate session fields from program defaults
        // For new sessions: only populate empty fields
        // For existing sessions: always populate when program changes (as requested by user)
        // Determine population strategy
        // - Edit sessions: always overwrite on program change
        // - New sessions:
        //   - First selection: only fill empty fields
        //   - Subsequent changes: overwrite (e.g., after "Add another like this")
        let shouldPopulate: (currentValue: string) => boolean;
        if (initialOffer) {
          shouldPopulate = () => true;
        } else {
          const hasPreviousSelection = !!prevVariationKey;
          shouldPopulate = hasPreviousSelection
            ? () => true
            : (currentValue: string) => !currentValue || currentValue === "";
        }
        
        
        // Populate description from program description
        if (selectedProgram.description) {
          const currentDescription = form.getValues('description');
          if (shouldPopulate(currentDescription)) {
            form.setValue('description', selectedProgram.description);
          }
        }
        
        // Populate eligibility notes from program eligibilityNotes
        if (selectedProgram.eligibilityNotes) {
          const currentEligibilityNotes = form.getValues('eligibilityNotes');
          if (shouldPopulate(currentEligibilityNotes)) {
            form.setValue('eligibilityNotes', selectedProgram.eligibilityNotes);
          }
        }
        
        // KB-4351 — Session website ← defaultSessionUrl, Registration ← regUrl. Never the program page.
        prefillSessionLinks(
          selectedProgram,
          (field, value) => form.setValue(field, value),
          (field, value) => !!value && shouldPopulate(form.getValues(field)),
        );
      }
    }
  }, [variationKey, verifiedLocation.variations, form, initialOffer]); // REMOVED startDate dependency

  return { 
    form,
    markFieldAsModified,
    resetFieldModified,
    isFieldModified,
    resetFieldToProgram,
    isEqualToProgramValue,
    resetDirtyState,
    isInitialized
  };
};
